import {
    AutoLayoutIterationClientData,
    AutoLayoutJobConfiguration,
    AutoLayoutJobMetadataError,
    AutoLayoutJobUid,
    AutoLayoutPreflightAnomaly,
    AutoLayoutStatus,
    DocumentUid,
    NodeUid,
    OrganizationUid,
    UserUid,
    type AutoLayoutJob,
} from "@buildwithflux/models";
import {cloneDeep} from "lodash";
import {createStore, type StoreApi} from "zustand";

import {
    AutoLayoutClientJobState,
    type AutoLayoutClientJob,
    type AutoLayoutStoreService,
    type AutoLayoutStoreState,
} from "./AutoLayoutStoreService";

/**
 * DeepPCB implementation of the centralized auto-layout store service.
 * Manages all auto-layout job state and shared data through a clean service API.
 * Uses Zustand store internally but exposes only business methods.
 */
export class DeepPcbAutoLayoutStoreService implements AutoLayoutStoreService {
    private readonly store: StoreApi<AutoLayoutStoreState>;

    constructor() {
        this.store = createStore<AutoLayoutStoreState>()(() => ({
            currentJob: undefined,
            sliderPosition: 0,
        }));
    }

    // Store access
    public getStore(): StoreApi<AutoLayoutStoreState> {
        return this.store;
    }

    // Job existence queries
    public hasActiveJob(): boolean {
        return this.store.getState().currentJob !== undefined;
    }

    public getCurrentJob(): AutoLayoutClientJob | undefined {
        const state = this.store.getState();
        return state.currentJob ? cloneDeep(state.currentJob) : undefined;
    }

    // Job lifecycle management
    public startJob(
        projectUid: DocumentUid,
        userUid: UserUid,
        organizationUid: OrganizationUid | undefined,
        layoutNodeUid: NodeUid,
        config: AutoLayoutJobConfiguration,
        jobUid?: AutoLayoutJobUid,
    ): void {
        const newJob: AutoLayoutClientJob = {
            // Job identification
            jobUid: jobUid,
            projectUid,
            userUid,
            organizationUid,
            layoutNodeUid,
            config,

            // Job runtime state
            state: AutoLayoutClientJobState.idle,
            creditsUsed: 0,
            iterations: [],
            currentIterationIndex: 0,
            loadingIteration: false,
            preflightAnomalies: undefined,
            metadataErrors: [],
            startTime: new Date(),
        };

        this.store.setState({
            currentJob: newJob,
        });
    }

    public loadFromBackendJob(backendJob: AutoLayoutJob, layoutNodeUid: string): AutoLayoutClientJob {
        const newJob: AutoLayoutClientJob = {
            // Job identification from backend
            jobUid: backendJob.jobUid,
            projectUid: backendJob.projectUid,
            userUid: backendJob.submittedByUserUid,
            organizationUid: backendJob.userOrganizationUid,
            layoutNodeUid,
            config: backendJob.configuration,

            // Job runtime state - sync initial state from backend
            state: this.mapBackendStatusToClientState(backendJob.jobStatus),
            creditsUsed: 0,
            iterations: [],
            currentIterationIndex: 0,
            loadingIteration: false,
            preflightAnomalies: undefined,
            metadataErrors: [],
            startTime: undefined,
        };

        this.store.setState({
            currentJob: newJob,
        });

        this.syncFromBackend(backendJob);

        return newJob;
    }

    public resetStore(): void {
        this.store.setState({
            currentJob: undefined,
            sliderPosition: 0,
        });
    }

    // Job UID management
    public setJobUid(jobUid: AutoLayoutJobUid | undefined): void {
        this.requireActiveJob();
        this.store.setState((state) => ({
            currentJob: state.currentJob
                ? {
                      ...state.currentJob,
                      jobUid,
                  }
                : undefined,
        }));
    }

    // Job state management
    public setState(newState: AutoLayoutClientJobState): void {
        this.requireActiveJob();
        this.store.setState((state) => ({
            currentJob: state.currentJob
                ? {
                      ...state.currentJob,
                      state: newState,
                  }
                : undefined,
        }));
    }

    public getCurrentState(): AutoLayoutClientJobState | undefined {
        return this.store.getState().currentJob?.state;
    }

    // Credits management
    public getCreditsUsed(): number {
        return this.store.getState().currentJob?.creditsUsed ?? 0;
    }

    public updateCreditsUsed(credits: number): void {
        this.requireActiveJob();
        this.store.setState((state) => ({
            currentJob: state.currentJob
                ? {
                      ...state.currentJob,
                      creditsUsed: credits,
                  }
                : undefined,
        }));
    }

    // Iteration management
    public getIterations(): AutoLayoutIterationClientData[] {
        return this.store.getState().currentJob?.iterations ?? [];
    }

    public addIterations(newIterations: AutoLayoutIterationClientData[]): void {
        this.requireActiveJob();

        if (newIterations.length === 0) {
            return;
        }

        this.store.setState((state) => ({
            currentJob: state.currentJob
                ? {
                      ...state.currentJob,
                      iterations: [...state.currentJob.iterations, ...newIterations].sort((a, b) => a.order - b.order),
                  }
                : undefined,
        }));
    }

    public updateIteration(iterationData: AutoLayoutIterationClientData): void {
        this.requireActiveJob();

        this.store.setState((state) => {
            if (!state.currentJob) return {};

            const currentIterations = state.currentJob.iterations;
            const index = currentIterations.findIndex((iter) => iter.hash === iterationData.hash);

            if (index === -1) {
                return {};
            }

            const updatedIterations = [...currentIterations];
            updatedIterations[index] = iterationData;

            return {
                currentJob: {
                    ...state.currentJob,
                    iterations: updatedIterations,
                },
            };
        });
    }

    public getCurrentIterationIndex(): number | undefined {
        return this.store.getState().currentJob?.currentIterationIndex;
    }

    public setCurrentIterationIndex(index: number): void {
        this.requireActiveJob();

        this.store.setState((state) => {
            if (!state.currentJob) return {};

            return {
                currentJob: {
                    ...state.currentJob,
                    currentIterationIndex: index,
                },
            };
        });
    }

    public setLoadingIteration(loading: boolean): void {
        this.requireActiveJob();
        this.store.setState((state) => ({
            currentJob: state.currentJob
                ? {
                      ...state.currentJob,
                      loadingIteration: loading,
                  }
                : undefined,
        }));
    }

    // Backend synchronization
    public syncFromBackend(backendJob: AutoLayoutJob): void {
        const currentJob = this.store.getState().currentJob;
        if (!currentJob || currentJob.jobUid !== backendJob.jobUid) {
            throw new Error(`Current job UID ${currentJob?.jobUid} does not match backend
    job UID ${backendJob.jobUid}`);
        }

        const updates: Partial<AutoLayoutClientJob> = {};

        // Handle special case for outOfCredit before mapping
        if (backendJob.jobStatus === AutoLayoutStatus.outOfCredit) {
            updates.state = backendJob.needsPaidCreditConsent
                ? AutoLayoutClientJobState.noCreditsAwaitingConsent
                : AutoLayoutClientJobState.noCredits;
        } else {
            // Map backend status to client state
            updates.state = this.mapBackendStatusToClientState(backendJob.jobStatus);
        }

        // Sync preflight anomalies
        if ("preflight" in backendJob && backendJob.preflight?.anomalies) {
            updates.preflightAnomalies = backendJob.preflight.anomalies;
        }

        // Sync start time
        if ("startTime" in backendJob && backendJob.startTime && currentJob.startTime === undefined) {
            updates.startTime = new Date(backendJob.startTime);
        }

        this.store.setState((state) => ({
            currentJob: state.currentJob
                ? {
                      ...state.currentJob,
                      ...updates,
                  }
                : undefined,
        }));
    }

    // Error management
    public getMetadataErrors(): ReadonlyArray<AutoLayoutJobMetadataError> {
        return this.store.getState().currentJob?.metadataErrors ?? [];
    }

    public setMetadataErrors(errors: AutoLayoutJobMetadataError[]): void {
        this.requireActiveJob();
        this.store.setState((state) => ({
            currentJob: state.currentJob
                ? {
                      ...state.currentJob,
                      metadataErrors: errors,
                  }
                : undefined,
        }));
    }

    // Preflight anomalies
    public getPreflightAnomalies(): AutoLayoutPreflightAnomaly[] | undefined {
        return this.store.getState().currentJob?.preflightAnomalies;
    }

    // Slider position management (shared state)
    public getSliderPosition(): number {
        return this.store.getState().sliderPosition;
    }

    public setSliderPosition(position: number): void {
        this.store.setState({
            sliderPosition: position,
        });
    }

    // State queries (convenience methods)
    public canPause(): boolean {
        const currentJob = this.store.getState().currentJob;
        return currentJob?.state === AutoLayoutClientJobState.running;
    }

    public canResume(): boolean {
        const currentJob = this.store.getState().currentJob;
        return currentJob?.state === AutoLayoutClientJobState.paused;
    }

    public isActive(): boolean {
        const currentJob = this.store.getState().currentJob;
        return currentJob
            ? [AutoLayoutClientJobState.running, AutoLayoutClientJobState.paused].includes(currentJob.state)
            : false;
    }

    public isTerminal(): boolean {
        const currentJob = this.store.getState().currentJob;
        return currentJob
            ? [
                  AutoLayoutClientJobState.applied,
                  AutoLayoutClientJobState.canceled,
                  AutoLayoutClientJobState.canceling,
                  AutoLayoutClientJobState.complete,
                  AutoLayoutClientJobState.error,
              ].includes(currentJob.state)
            : false;
    }

    // Private helper methods
    private requireActiveJob(): void {
        if (!this.hasActiveJob()) {
            throw new Error("No active auto-layout job exists");
        }
    }

    /**
     * Maps backend AutoLayoutStatus to client-side AutoLayoutClientJobState
     */
    private mapBackendStatusToClientState(status: keyof typeof AutoLayoutStatus): AutoLayoutClientJobState {
        switch (status) {
            case AutoLayoutStatus.allocated:
            case AutoLayoutStatus.populated:
            case AutoLayoutStatus.submitted:
            case AutoLayoutStatus.ready:
            case AutoLayoutStatus.booting:
                return AutoLayoutClientJobState.waiting;
            case AutoLayoutStatus.started:
            case AutoLayoutStatus.restarted:
            case AutoLayoutStatus.generating:
                return AutoLayoutClientJobState.running;
            case AutoLayoutStatus.pausing:
                return AutoLayoutClientJobState.pausing;
            case AutoLayoutStatus.paused:
                return AutoLayoutClientJobState.paused;
            case AutoLayoutStatus.outOfCredit:
                // Note: Special handling for this case is done in syncFromBackend
                return AutoLayoutClientJobState.noCredits;
            case AutoLayoutStatus.resuming:
                return AutoLayoutClientJobState.resuming;
            case AutoLayoutStatus.converged:
            case AutoLayoutStatus.timeout:
                return AutoLayoutClientJobState.complete;
            case AutoLayoutStatus.failed:
                return AutoLayoutClientJobState.error;
            case AutoLayoutStatus.discarded:
                return AutoLayoutClientJobState.canceled;
            case AutoLayoutStatus.applied:
                return AutoLayoutClientJobState.applied;
        }
    }
}
